import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { collection, query, where, doc, getDoc, updateDoc, serverTimestamp, limit } from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { db, storage } from '../firebase';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { dailyActivityService } from '../services/dailyActivityService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ArtworkReactions from './ArtworkReactions';
import { getReactionArtworkId } from '../utils/reactionArtworkId';
import { PAGING } from '../config/paging';
import { trackEvent } from '../utils/observability';
import { API_ENDPOINTS } from '../config/backendConfig';
import { FaTimes, FaExpand } from 'react-icons/fa';
import { getDisplayNameForUser } from '../utils/consentUtils';
import {
  coLearningExpandedBackdropProps,
  coLearningNestedBackdropProps,
  coLearningWallBackdropProps,
} from '../ui/coLearningModalTheme';
import { useArtistSalonAgeFilter } from '../context/ArtistSalonAgeFilterContext';

/** Prefer denormalized submission fields; never show raw UID fragments. */
function pickSubmissionArtistName(c) {
  if (!c) return '';
  const fields = [c.artistName, c.userName, c.displayName, c.studentName, c.nickname, c.name];
  for (const f of fields) {
    if (typeof f === 'string' && f.trim()) return f.trim();
  }
  const em = c.email || c.userEmail;
  if (typeof em === 'string' && em.includes('@')) {
    const local = em.split('@')[0].trim();
    if (local) return local;
  }
  return '';
}

/** Firestore may store activityId as string ("33") or number (33) depending on when it was saved. */
function activityIdQueryVariants(activityId) {
  const variants = [];
  const asString = String(activityId);
  if (asString) variants.push(asString);
  const asNum = Number(activityId);
  if (!Number.isNaN(asNum) && !variants.some((v) => v === asNum)) {
    variants.push(asNum);
  }
  return variants;
}

const GLASS_BTN =
  'flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/55 text-[#141210] transition-all duration-200 ease-out hover:scale-[1.08] active:scale-95 disabled:pointer-events-none disabled:opacity-35';

const glassNavInline = {
  touchAction: 'manipulation',
  background: 'rgba(255, 255, 255, 0.72)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
};

const ActivityWallModal = ({ activityId, onClose, initialSubmissionId }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { filterSubmissions } = useArtistSalonAgeFilter();
  const [activity, setActivity] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const visibleSubmissions = useMemo(
    () => filterSubmissions(submissions),
    [submissions, filterSubmissions]
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [resolved, setResolved] = useState({ page: '', spread: '' });
  const [pageLoaded, setPageLoaded] = useState(false);
  const [artworkLoaded, setArtworkLoaded] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const currentIdRef = useRef(null);
  const initializedFromParamRef = useRef(false);
  const sortRequestIdRef = useRef(0);
  const [showUnshareConfirm, setShowUnshareConfirm] = useState(false);
  /** When submission has no artistName/email, hydrated from artistProfiles / users. */
  const [profileArtistName, setProfileArtistName] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'activities', String(activityId)));
        if (snap.exists()) setActivity({ id: snap.id, ...snap.data() });
        else setActivity({ id: String(activityId), title: `Daily Art Activity ${activityId}`, description: 'Daily creative activity', prerequisites: [], steps: [], tips: [] });
      } catch (e) {
        setActivity({ id: String(activityId), title: `Daily Art Activity ${activityId}`, description: 'Daily creative activity', prerequisites: [], steps: [], tips: [] });
      }
    };
    load();
  }, [activityId]);

  // Resolve Storage paths (previewPagePath/previewSpreadPath) or existing urls
  useEffect(() => {
    const resolveUrls = async () => {
      if (!activity) return;
      try {
        let pageUrl = activity.previewPageUrl || activity.coverImageUrl || activity.imageUrl || '';
        let spreadUrl = activity.previewSpreadUrl || '';

        if (!pageUrl && activity.previewPagePath) {
          pageUrl = await getDownloadURL(storageRef(storage, activity.previewPagePath));
        }
        if (!spreadUrl && activity.previewSpreadPath) {
          spreadUrl = await getDownloadURL(storageRef(storage, activity.previewSpreadPath));
        }

        // Last-resort fallback: use dailyActivityService imageUrl for this id
        if (!pageUrl) {
          try {
            const all = await dailyActivityService.getAllActivities();
            const match = all.find(a => String(a.id) === String(activityId));
            if (match?.imageUrl) pageUrl = match.imageUrl;
          } catch (_) {}
        }

        setResolved({ page: pageUrl, spread: spreadUrl });
        setPageLoaded(false);
        setArtworkLoaded(false);
      } catch (_) {
        setResolved({ page: activity?.imageUrl || '', spread: '' });
        setPageLoaded(false);
        setArtworkLoaded(false);
      }
    };
    resolveUrls();
  }, [activity, activityId]);

  // Load public submissions (string + numeric activityId) and manage like state
  useEffect(() => {
    const bucketsRef = { byKey: {} };
    const idVariants = activityIdQueryVariants(activityId);

    const toMillis = (t) => {
      try {
        if (!t) return 0;
        if (typeof t?.toMillis === 'function') return t.toMillis();
        if (t?._seconds) return t._seconds * 1000 + (t._nanoseconds || 0) / 1000000;
        if (t instanceof Date) return t.getTime();
        const d = new Date(t);
        return Number.isNaN(d.getTime()) ? 0 : d.getTime();
      } catch (_) {
        return 0;
      }
    };

    const applyMergedSubmissions = async () => {
      const mergedById = new Map();
      Object.values(bucketsRef.byKey).forEach((list) => {
        (list || []).forEach((s) => {
          if (s.isPublic === true) mergedById.set(s.id, s);
        });
      });
      const createdSorted = Array.from(mergedById.values()).sort(
        (a, b) => toMillis(b.createdAt) - toMillis(a.createdAt)
      );

      const prevCurrentId = currentIdRef.current;
      setSubmissions(createdSorted);

      if (!initializedFromParamRef.current && initialSubmissionId) {
        const idx = createdSorted.findIndex((s) => s.id === String(initialSubmissionId));
        if (idx !== -1) {
          setCurrentIndex(idx);
          currentIdRef.current = createdSorted[idx]?.id || null;
        }
        initializedFromParamRef.current = true;
      } else if (prevCurrentId) {
        const newIdx = createdSorted.findIndex((s) => s.id === prevCurrentId);
        setCurrentIndex(newIdx !== -1 ? newIdx : 0);
      } else {
        setCurrentIndex(createdSorted.length ? 0 : 0);
      }

      const runId = ++sortRequestIdRef.current;
      try {
        const totals = await Promise.all(
          createdSorted.map(async (submission) => {
            const reactionArtworkId = getReactionArtworkId(submission);
            if (!reactionArtworkId) return [submission.id, 0];
            try {
              const resp = await fetch(
                `${API_ENDPOINTS.getKudosCount}?artworkId=${encodeURIComponent(reactionArtworkId)}`
              );
              const data = await resp.json();
              if (data?.success) return [submission.id, data?.totalReactions ?? 0];
              return [submission.id, 0];
            } catch (_) {
              return [submission.id, 0];
            }
          })
        );

        if (runId !== sortRequestIdRef.current) return;

        const reactionMap = {};
        totals.forEach(([id, total]) => {
          reactionMap[id] = total;
        });

        const sortedByReactions = [...createdSorted].sort((a, b) => {
          const ra = reactionMap[a?.id] ?? 0;
          const rb = reactionMap[b?.id] ?? 0;
          if (rb !== ra) return rb - ra;
          return toMillis(b?.createdAt) - toMillis(a.createdAt);
        });

        setSubmissions(sortedByReactions);

        const pickIdx = () => {
          if (prevCurrentId) {
            const i = sortedByReactions.findIndex((s) => s.id === prevCurrentId);
            return i !== -1 ? i : 0;
          }
          return sortedByReactions.length ? 0 : 0;
        };
        const idx = pickIdx();
        setCurrentIndex(idx);
        currentIdRef.current = sortedByReactions[idx]?.id || null;
        initializedFromParamRef.current = true;
      } catch (_) {
        // Keep createdAt ordering if reaction sort fails
      }
    };

    const unsubs = idVariants.map((aid) => {
      const bucketKey = typeof aid === 'number' ? `num:${aid}` : `str:${aid}`;
      const q = query(
        collection(db, 'submissions'),
        where('activityId', '==', aid),
        where('isPublic', '==', true),
        limit(PAGING.PUBLIC_SUBMISSIONS_PER_ACTIVITY_LIMIT)
      );
      return onSnapshot(
        q,
        (snap) => {
          bucketsRef.byKey[bucketKey] = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          applyMergedSubmissions();
        },
        undefined,
        { purpose: 'ActivityWallModal-submissions', component: 'ActivityWallModal', essential: true }
      );
    });

    return () => unsubs.forEach((u) => u());
  }, [activityId, initialSubmissionId]);

  // Analytics: track when the activity wall modal opens
  useEffect(() => {
    if (activityId) {
      trackEvent('activity_wall_modal_open', {
        activityId: String(activityId),
      });
    }
  }, [activityId]);

  const current = visibleSubmissions[currentIndex] || null;
  useEffect(() => {
    currentIdRef.current = current?.id || null;
  }, [current]);

  useEffect(() => {
    if (!current?.id) return;
    setArtworkLoaded(false);
  }, [current?.id]);

  /** Per-activity print layout; mobile uses a wider right-hand overlay when not configured. */
  const overlayBox = useMemo(() => {
    const custom = activity?.overlayBox;
    if (custom && typeof custom === 'object' && (custom.width != null || custom.left != null)) {
      return custom;
    }
    return { top: 4, left: 50, width: 49, height: 92 };
  }, [activity?.overlayBox]);

  useEffect(() => {
    let cancelled = false;
    if (!current) {
      setProfileArtistName('');
      return;
    }
    const inline = pickSubmissionArtistName(current);
    if (inline) {
      setProfileArtistName('');
      return;
    }
    const uid = current.uid || current.userId;
    const sid = current.id;
    if (!uid) {
      setProfileArtistName('Young artist');
      return;
    }
    setProfileArtistName('');
    getDisplayNameForUser(uid)
      .then((n) => {
        if (cancelled || currentIdRef.current !== sid) return;
        const t = typeof n === 'string' ? n.trim() : '';
        const cleaned = t && t !== 'Artist' ? t : 'Young artist';
        setProfileArtistName(cleaned);
      })
      .catch(() => {
        if (!cancelled && currentIdRef.current === sid) setProfileArtistName('Young artist');
      });
    return () => {
      cancelled = true;
    };
  }, [current]);

  const handleArtistClick = (uid) => {
    if (uid) navigate(`/artist-public-gallery/${uid}`);
  };

  const nextSubmission = () => {
    if (currentIndex < visibleSubmissions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const prevSubmission = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showUnshareConfirm) {
          setShowUnshareConfirm(false);
          return;
        }
        if (isExpanded) {
          setIsExpanded(false);
          return;
        }
        onClose();
      } else if (e.key === 'ArrowLeft') {
        prevSubmission();
      } else if (e.key === 'ArrowRight') {
        nextSubmission();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, visibleSubmissions.length, showUnshareConfirm, isExpanded, onClose]);

  const formatSubmissionDate = (c) => {
    if (!c?.createdAt) return null;
    let date;
    if (c.createdAt?.toDate) date = c.createdAt.toDate();
    else if (c.createdAt?._seconds) date = new Date(c.createdAt._seconds * 1000);
    else date = new Date(c.createdAt);
    if (isNaN(date.getTime())) return null;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const submissionArtistLabel =
    current &&
    (pickSubmissionArtistName(current) || profileArtistName || 'Young artist');

  const submissionArtistInitial =
    submissionArtistLabel && submissionArtistLabel.trim()
      ? submissionArtistLabel.trim().charAt(0).toUpperCase()
      : '?';

  return (
    <>
      <style>{`
        .activity-wall-instructions-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(190, 180, 165, 0.55) transparent;
        }
        .activity-wall-instructions-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .activity-wall-instructions-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .activity-wall-instructions-scroll::-webkit-scrollbar-thumb {
          background-color: rgba(190, 180, 165, 0.35);
          border-radius: 9999px;
        }
        .activity-wall-instructions-scroll:hover::-webkit-scrollbar-thumb {
          background-color: rgba(170, 155, 140, 0.55);
        }
      `}</style>

      {typeof document !== 'undefined' &&
        createPortal(
          <>
      <div
        {...coLearningWallBackdropProps}
        onClick={onClose}
        data-testid="activity-wall-modal"
      >
        <div className="flex w-full max-w-[calc(1400px+7rem)] flex-col items-stretch justify-center gap-3 lg:flex-row lg:items-center lg:gap-2">
          {visibleSubmissions.length > 1 && (
            <div className="order-3 hidden shrink-0 justify-center lg:order-1 lg:flex lg:w-14">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  prevSubmission();
                }}
                disabled={currentIndex === 0}
                className={GLASS_BTN}
                style={glassNavInline}
                aria-label="Previous submission"
              >
                <ChevronLeft className="h-5 w-5" strokeWidth={1.75} aria-hidden />
              </button>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="order-1 flex h-[100dvh] max-h-[100dvh] min-h-0 w-full min-w-0 max-w-[1400px] flex-col overflow-hidden rounded-none sm:rounded-[32px] lg:order-2 lg:h-[92vh] lg:max-h-[92vh]"
            style={{
              background: 'linear-gradient(180deg, #fffdf8 0%, #fffaf2 100%)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 24px rgba(0, 0, 0, 0.06)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <header className="relative z-[5] flex shrink-0 items-center justify-between gap-3 border-b border-[#ebe6dc]/80 px-4 py-2 sm:px-6 sm:py-2.5">
              <div className="min-w-0 flex-1 pr-2">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0">
                  <h3 className="truncate text-base font-semibold leading-tight tracking-tight text-[#2f2a24] sm:text-lg">
                    {activity?.title || `Daily Activity ${activityId}`}
                  </h3>
                  {current && visibleSubmissions.length > 0 && (
                    <span className="shrink-0 text-xs font-medium text-[#8b867f] sm:text-sm">
                      · {currentIndex + 1} of {visibleSubmissions.length}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#5e5a54] transition-all duration-200 ease-out hover:scale-105 hover:bg-white/85 hover:text-[#2f2a24]"
                style={{
                  background: 'rgba(255,255,255,0.55)',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                }}
                aria-label="Close"
              >
                <FaTimes className="h-4 w-4" />
              </button>
            </header>

            {!resolved.page ? (
              <div className="flex flex-1 flex-col items-center justify-center px-8 py-16 text-center">
                <p className="text-lg font-semibold text-[#2f2a24]">Opening your activity sheet</p>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-[#5e5a54]">
                  Activity page not available yet — this usually resolves in a moment.
                </p>
              </div>
            ) : (
              <div className="activity-wall-instructions-scroll flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
                <div className="flex min-h-0 flex-1 flex-col px-2 py-2 sm:px-5 sm:py-3">
                  <motion.div
                    key={resolved.page}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    className="relative mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col"
                  >
                    <div className="relative min-h-0 flex-1 overflow-hidden rounded-2xl bg-[#fffdf8] ring-1 ring-[#ebe6dc]/50 shadow-[0_10px_40px_rgba(0,0,0,0.07)]">
                      {/* Single sheet: activity fills frame; artwork overlaid on the right (all breakpoints) */}
                      <div className="relative h-full min-h-[calc(100dvh-10.5rem)] w-full sm:min-h-[calc(92vh-7.25rem)]">
                        <img
                          src={resolved.page}
                          alt="Activity"
                          className={`absolute inset-0 h-full w-full object-contain object-center transition-opacity duration-150 ${pageLoaded ? 'opacity-100' : 'opacity-0'}`}
                          decoding="async"
                          fetchPriority="high"
                          onLoad={() => setPageLoaded(true)}
                          onError={() => {
                            console.error('Failed to load activity page:', resolved.page);
                            setPageLoaded(true);
                          }}
                        />

                        {current && (
                          <button
                            type="button"
                            onClick={() => setIsExpanded(true)}
                            className="absolute right-2 top-2 z-40 flex h-9 w-9 items-center justify-center rounded-full text-[#5e5a54] transition-all duration-200 hover:scale-105 hover:bg-white/90 hover:text-[#2f2a24] sm:right-3 sm:top-3 sm:h-10 sm:w-10"
                            style={{
                              background: 'rgba(255,255,255,0.82)',
                              backdropFilter: 'blur(10px)',
                              WebkitBackdropFilter: 'blur(10px)',
                              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                            }}
                            title="Expand artwork"
                          >
                            <FaExpand className="h-4 w-4" />
                          </button>
                        )}

                        {current && (
                          <button
                            type="button"
                            onClick={() => setIsExpanded(true)}
                            className={`absolute z-30 transition-opacity duration-150 ${artworkLoaded ? 'opacity-100' : 'opacity-0'}`}
                            style={{
                              top: `${overlayBox.top ?? 4}%`,
                              left: `${overlayBox.left ?? 50}%`,
                              width: `${overlayBox.width ?? 49}%`,
                              height: `${overlayBox.height ?? 92}%`,
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              cursor: 'zoom-in',
                              pointerEvents: artworkLoaded ? 'auto' : 'none',
                            }}
                            title="Click to expand artwork"
                          >
                            <img
                              key={current.id}
                              src={current.thumbUrl || current.imageUrl}
                              alt="Submission on activity sheet"
                              className="h-full w-full rounded-sm object-contain object-center shadow-[0_6px_20px_rgba(0,0,0,0.12)]"
                              loading="eager"
                              decoding="async"
                              fetchPriority="high"
                              onLoad={() => setArtworkLoaded(true)}
                              onError={() => {
                                console.error('Failed to load submission image:', current.imageUrl);
                                setArtworkLoaded(true);
                              }}
                            />
                          </button>
                        )}

                        {!current && (
                          <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#fffdf8]/92 backdrop-blur-[2px]">
                            <div className="max-w-sm px-6 text-center">
                              <p className="text-lg font-semibold text-[#2f2a24]">No public submissions yet</p>
                              <p className="mt-2 text-sm leading-relaxed text-[#5e5a54]">
                                Be the first to share your artwork for this activity.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>

                  {visibleSubmissions.length > 1 && (
                    <div className="mt-3 flex justify-center gap-10 pb-1 lg:hidden">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          prevSubmission();
                        }}
                        disabled={currentIndex === 0}
                        className={GLASS_BTN}
                        style={glassNavInline}
                        aria-label="Previous submission"
                      >
                        <ChevronLeft className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          nextSubmission();
                        }}
                        disabled={currentIndex === visibleSubmissions.length - 1}
                        className={GLASS_BTN}
                        style={glassNavInline}
                        aria-label="Next submission"
                      >
                        <ChevronRight className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {current && (
              <footer
                className="flex h-[60px] max-h-[64px] shrink-0 items-center gap-3 border-t border-white/50 px-4 sm:px-6"
                style={{
                  background: 'rgba(255, 255, 255, 0.72)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                  borderTopLeftRadius: '20px',
                  borderTopRightRadius: '20px',
                  boxShadow: '0 -8px 24px rgba(0,0,0,0.04)',
                }}
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  {current.artistPhotoURL ? (
                    <img
                      src={current.artistPhotoURL}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-[#ebe6dc]"
                    />
                  ) : (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-100 to-orange-100 text-sm font-semibold text-[#5e5a54] ring-1 ring-[#ebe6dc]">
                      {submissionArtistInitial}
                    </div>
                  )}
                  <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 leading-tight">
                    <button
                      type="button"
                      onClick={() => handleArtistClick(current.uid || current.userId)}
                      className="block max-w-full truncate text-left text-sm font-medium leading-snug text-[#2f2a24] transition-colors hover:text-[#5e5a54]"
                    >
                      {submissionArtistLabel}
                    </button>
                    {formatSubmissionDate(current) && (
                      <p className="text-xs leading-snug text-[#8b867f]">{formatSubmissionDate(current)}</p>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <ArtworkReactions
                    artworkId={getReactionArtworkId(current)}
                    artistId={current.uid || current.userId}
                    enableComments={false}
                  />
                  {(current.uid === user?.uid || current.userId === user?.uid) && (
                    <button
                      type="button"
                      onClick={() => setShowUnshareConfirm(true)}
                      className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-medium text-[#5e5a54] ring-1 ring-[#ebe6dc] transition-all hover:bg-white hover:text-[#2f2a24]"
                    >
                      Unshare
                    </button>
                  )}
                </div>
              </footer>
            )}
          </motion.div>

          {visibleSubmissions.length > 1 && (
            <div className="order-4 hidden shrink-0 justify-center lg:order-3 lg:flex lg:w-14">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  nextSubmission();
                }}
                disabled={currentIndex === visibleSubmissions.length - 1}
                className={GLASS_BTN}
                style={glassNavInline}
                aria-label="Next submission"
              >
                <ChevronRight className="h-5 w-5" strokeWidth={1.75} aria-hidden />
              </button>
            </div>
          )}
        </div>
      </div>

      {isExpanded && current && (
          <div
            role="presentation"
            {...coLearningExpandedBackdropProps}
            onClick={() => setIsExpanded(false)}
          >
            <div
              className="relative z-[1] mx-auto flex h-full w-full max-w-7xl items-center justify-center gap-2 px-1 sm:gap-4 sm:px-3"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="absolute right-3 top-3 z-[510] flex h-11 w-11 items-center justify-center rounded-full text-[#141210] shadow-[0_8px_28px_rgba(0,0,0,0.12)] transition-transform hover:scale-105 sm:right-5 sm:top-5 sm:h-12 sm:w-12"
                style={{
                  background: 'rgba(255,255,255,0.88)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                }}
                aria-label="Close expanded view"
              >
                <FaTimes className="h-5 w-5" />
              </button>

              {visibleSubmissions.length > 1 && (
                <button
                  type="button"
                  onClick={prevSubmission}
                  disabled={currentIndex === 0}
                  className={`shrink-0 ${GLASS_BTN} !h-11 !w-11 sm:!h-12 sm:!w-12`}
                  style={glassNavInline}
                  aria-label="Previous submission"
                >
                  <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.65} aria-hidden />
                </button>
              )}

              <div className="relative flex min-h-0 min-w-0 flex-1 flex-col items-center justify-center pb-[4.5rem] sm:pb-20">
                <img
                  src={current.imageUrl}
                  alt="Expanded submission"
                  className="max-h-[min(78dvh,88vh,920px)] max-w-full object-contain drop-shadow-2xl"
                  onError={() => {
                    console.error('Failed to load submission image:', current.imageUrl);
                  }}
                />

                <div className="pointer-events-none absolute bottom-3 left-0 right-0 z-[510] flex justify-center sm:bottom-5">
                  <div
                    className="pointer-events-auto max-w-[min(100%,20rem)] rounded-2xl px-4 py-2 text-center text-xs font-medium text-[#141210] ring-1 ring-white/40 sm:max-w-none sm:px-5 sm:py-2.5 sm:text-sm"
                    style={{
                      background: 'rgba(255,255,255,0.88)',
                      backdropFilter: 'blur(14px)',
                      WebkitBackdropFilter: 'blur(14px)',
                      boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
                    }}
                  >
                    {currentIndex + 1} of {visibleSubmissions.length} ·{' '}
                    {pickSubmissionArtistName(current) || profileArtistName || 'Young artist'}
                  </div>
                </div>
              </div>

              {visibleSubmissions.length > 1 && (
                <button
                  type="button"
                  onClick={nextSubmission}
                  disabled={currentIndex === visibleSubmissions.length - 1}
                  className={`shrink-0 ${GLASS_BTN} !h-11 !w-11 sm:!h-12 sm:!w-12`}
                  style={glassNavInline}
                  aria-label="Next submission"
                >
                  <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.65} aria-hidden />
                </button>
              )}
            </div>
          </div>
      )}

      {showUnshareConfirm && (
        <div {...coLearningNestedBackdropProps}>
          <div
            className="w-full max-w-md overflow-hidden rounded-2xl shadow-2xl ring-1 ring-[#ebe6dc]"
            style={{
              background: 'linear-gradient(180deg, #fffdf8 0%, #fffaf2 100%)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 24px rgba(0, 0, 0, 0.06)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-6">
              <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-100 to-orange-100 text-sm font-bold text-[#2f2a24] ring-1 ring-[#ebe6dc]">
                A
              </div>
              <h3 className="text-xl font-semibold text-[#2f2a24]">Unshare from co-learning?</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#5e5a54]">
                Unsharing removes this artwork from the space. You can share again later.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowUnshareConfirm(false)}
                  className="rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-[#5e5a54] ring-1 ring-[#ebe6dc] transition-colors hover:bg-white hover:text-[#2f2a24]"
                >
                  Keep public
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      if (!current || !user) return;
                      await updateDoc(doc(db, 'submissions', current.id), {
                        isPublic: false,
                        updatedAt: serverTimestamp(),
                      });

                      const updated = submissions.filter(s => s.id !== current.id);
                      setSubmissions(updated);
                      setCurrentIndex(0);
                      setShowUnshareConfirm(false);
                      if (updated.length === 0) onClose();
                    } catch (e) {
                      console.error('Failed to unshare daily activity artwork:', e);
                      alert('Failed to unshare artwork. Please try again.');
                    }
                  }}
                  className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-md transition-transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  Unshare
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          </>,
          document.body
        )}
    </>
  );
};

export default ActivityWallModal;
